/**
 * PAGE: LISTE VÉHICULES (VERSION REFACTORISÉE)
 */

import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import {
  ColumnSelectorModal,
  CreateVehicleModal,
  FiltersModal,
  ImportCSVModal,
} from '../components/modals';
import { ActionButtons, PageContent, PageHeader, PageLayout, Pagination } from '../components/page';
import { EnterpriseTable } from '../components/tables';
import { CheckboxCell, ActionsCell, EditableInputCell } from '../components/tables/cells';
import { TableCell } from '../components/table';
import {
  BulkActionsBar,
  ConfirmDialog,
  ErrorState,
  LoadingState,
  toast,
  Select,
} from '../components/ui';
import { useEntityList } from '../hooks/useEntityList';
import { useVehicles } from '../hooks/useVehicles';
import { useCrews } from '../hooks/useCrews';
import { useSupabase } from '../hooks/useSupabase';
import { usePermissions } from '../hooks/usePermissions';
import type { Vehicle } from '../types/vehicle';
import { VEHICLE_CONFIG } from '../config/entities/vehicle.config';
import {
  generateCSVColumns,
  generateCSVTransformers,
  generateImportConfig,
  generateFilterConfigs,
  generateColumnOptions,
} from '../lib/entity-config';
import type { FilterConfig } from '../types/filters';

export function VehiclesListPage() {
  const supabase = useSupabase();
  const {
    data: vehicles,
    loading,
    error,
    create,
    update,
    remove,
    removeMany,
    refreshVehicles,
  } = useVehicles();
  const { data: crews } = useCrews();

  // Permissions utilisateur
  const {
    canCreateEntity,
    canUpdateEntity,
    canDeleteEntity,
    canImportData,
    canExportData,
    canDeleteBulk,
  } = usePermissions();

  // Générer automatiquement depuis VEHICLE_CONFIG + filtres customs
  const filterConfigs = useMemo(() => {
    const baseFilters = generateFilterConfigs(VEHICLE_CONFIG);

    // Filtre custom pour l'équipage affecté
    const crewFilterOptions = [
      { value: '', label: 'Non affecté' },
      ...(crews?.map((crew) => ({
        value: crew.id,
        label:
          crew.name || (crew.crew_number ? `Équipage ${crew.crew_number}` : 'Équipage sans nom'),
      })) || []),
    ];

    const crewFilter: FilterConfig = {
      field: 'crew_id',
      label: 'Équipage affecté',
      type: 'select',
      operators: ['equals', 'notEquals', 'isNotNull', 'isNull'],
      options: crewFilterOptions,
    };

    return [...baseFilters, crewFilter];
  }, [crews]);
  const csvColumns = useMemo(() => generateCSVColumns(VEHICLE_CONFIG), []);

  // Générer les transformers de base + ajouter le custom pour crew_id
  const csvTransformers = useMemo(() => {
    const baseTransformers = generateCSVTransformers(VEHICLE_CONFIG);
    return {
      ...baseTransformers,
      crew_id: (value: string | null) => {
        if (!value) return 'Non affecté';
        const crew = crews?.find((c) => c.id === value);
        return crew?.name || '';
      },
    };
  }, [crews]);

  const importConfig = useMemo(() => generateImportConfig(VEHICLE_CONFIG), []);

  const entity = useEntityList({
    data: vehicles,
    loading,
    error,
    create,
    remove,
    removeMany,
    refresh: refreshVehicles,
    entityName: VEHICLE_CONFIG.entityName,
    entityNamePlural: VEHICLE_CONFIG.entityNamePlural,
    searchableFields: ['registration', 'notes', 'owner'] as (keyof Vehicle)[],
    filterConfigs,
    csvFileName: 'vehicules.csv',
    csvColumns,
    csvTransformers,
    importConfig,
  });

  const { handleDeleteClick } = entity;

  const handleUpdateVehicle = useCallback(
    async (id: string, field: keyof Vehicle, value: string | null) => {
      if (!update) return;
      try {
        await update(id, { [field]: value });
        // Rafraîchir la liste pour récupérer les données mises à jour
        await refreshVehicles();
        toast.success('Véhicule mis à jour');
      } catch {
        toast.error('Erreur lors de la mise à jour');
      }
    },
    [update, refreshVehicles]
  );

  // Vérifier si des actions sont possibles
  const hasAnyBulkAction = canDeleteBulk('vehicle') || canExportData('vehicle');

  const columns: ColumnDef<Vehicle>[] = useMemo(() => {
    const cols: ColumnDef<Vehicle>[] = [
      // Colonne checkbox uniquement si actions de masse possibles
      ...(hasAnyBulkAction
        ? ([
            {
              id: 'select',
              size: 60,
              header: ({ table }: any) => (
                <CheckboxCell
                  row={
                    {
                      getIsSelected: () => table.getIsAllRowsSelected(),
                      toggleSelected: () => table.toggleAllRowsSelected(),
                    } as any
                  }
                />
              ),
              cell: ({ row }: any) => <CheckboxCell row={row} />,
            },
          ] as ColumnDef<Vehicle>[])
        : []),
      {
        accessorKey: 'registration',
        header: 'Immatriculation',
        cell: ({ getValue }) => (
          <TableCell className="font-semibold">{getValue() as string}</TableCell>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'crew_id',
        header: 'Équipage affecté',
        cell: ({ row, getValue }) => {
          const crewId = getValue() as string | null;
          return (
            <Select
              variant="inline"
              value={crewId || ''}
              onChange={(e) => {
                const newCrewId = e.target.value || null;
                handleUpdateVehicle(row.original.id, 'crew_id', newCrewId);
              }}
              className="w-full"
            >
              <option value="">Non affecté</option>
              {crews?.map((crew) => (
                <option key={crew.id} value={crew.id}>
                  {crew.name ||
                    (crew.crew_number ? `Équipage ${crew.crew_number}` : 'Équipage sans nom')}
                </option>
              ))}
            </Select>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'circulation_date',
        header: 'Date de mise en circulation',
        cell: ({ row, getValue }) => (
          <EditableInputCell
            value={getValue() as string}
            type="date"
            onSave={(newValue) =>
              handleUpdateVehicle(row.original.id, 'circulation_date', newValue)
            }
            readOnly={!canUpdateEntity('vehicle')}
          />
        ),
        enableSorting: true,
        sortingFn: 'datetime',
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        cell: ({ row, getValue }) => (
          <EditableInputCell
            value={getValue() as string}
            onSave={(newValue) => handleUpdateVehicle(row.original.id, 'notes', newValue)}
            readOnly={!canUpdateEntity('vehicle')}
          />
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'owner',
        header: 'Propriétaire',
        cell: ({ row, getValue }) => (
          <EditableInputCell
            value={getValue() as string}
            onSave={(newValue) => handleUpdateVehicle(row.original.id, 'owner', newValue)}
            readOnly={!canUpdateEntity('vehicle')}
          />
        ),
        enableSorting: true,
      },
      {
        id: 'actions',
        header: 'Action',
        cell: ({ row }) => (
          <ActionsCell
            onDelete={() => handleDeleteClick(row.original.id)}
            canDelete={canDeleteEntity('vehicle')}
          />
        ),
      },
    ];

    // Filtrer la colonne "Actions" si l'utilisateur ne peut pas supprimer
    return canDeleteEntity('vehicle')
      ? cols
      : cols.filter((col) => {
          // Type guard pour vérifier si la colonne a un id
          const hasId = 'id' in col;
          return !hasId || col.id !== 'actions';
        });
  }, [
    handleUpdateVehicle,
    handleDeleteClick,
    crews,
    canDeleteEntity,
    canUpdateEntity,
    hasAnyBulkAction,
  ]);

  // Colonnes disponibles pour la sélection (générées automatiquement)
  const columnOptions = useMemo(
    () => generateColumnOptions(VEHICLE_CONFIG, entity.table.columnVisibility),
    [entity.table.columnVisibility]
  );

  const handleColumnVisibilityApply = (
    newColumns: Array<{ id: string; label: string; visible?: boolean }>
  ) => {
    const newVisibility: Record<string, boolean> = {};
    newColumns.forEach((col) => {
      newVisibility[col.id] = col.visible !== false;
    });
    entity.table.setColumnVisibility(newVisibility);
  };

  if (entity.loading) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Véhicules' }]} />
        <div className="w-full h-px bg-zinc-200" />
        <LoadingState message="Chargement des véhicules..." />
      </PageLayout>
    );
  }

  if (entity.error) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Véhicules' }]} />
        <div className="w-full h-px bg-zinc-200" />
        <ErrorState
          title="Erreur lors du chargement des véhicules"
          message={entity.error.message}
          onRetry={() => window.location.reload()}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Véhicules' }]} />
      <div className="w-full h-px bg-zinc-200" />

      <PageContent
        title="Véhicules"
        description="Gérez les véhicules et leurs affectations aux équipages"
        actions={
          <ActionButtons
            searchValue={entity.table.searchTerm}
            onSearchChange={entity.table.setSearchTerm}
            onDownload={canExportData('vehicle') ? entity.handleExportCSV : undefined}
            onUpload={
              canImportData('vehicle') ? () => entity.setIsImportModalOpen(true) : undefined
            }
            onFilter={() => entity.setIsFiltersModalOpen(true)}
            onColumns={() => entity.setIsColumnModalOpen(true)}
            onCreate={
              canCreateEntity('vehicle') ? () => entity.setIsCreateModalOpen(true) : undefined
            }
            activeFiltersCount={entity.table.activeFilters.length}
          />
        }
        footer={
          <Pagination
            currentPage={entity.table.currentPage}
            totalPages={entity.table.totalPages}
            itemsPerPage={entity.table.itemsPerPage}
            totalItems={entity.table.totalItems}
            displayedItems={entity.table.displayedItemsCount}
            onPageChange={entity.table.setCurrentPage}
            onItemsPerPageChange={entity.table.setItemsPerPage}
            itemsPerPageOptions={[10, 25, 50, 100]}
          />
        }
      >
        <EnterpriseTable
          data={entity.table.filteredData}
          columns={columns}
          onRowSelectionChange={entity.table.setSelectedItems}
          columnVisibility={entity.table.columnVisibility}
          onColumnVisibilityChange={entity.table.setColumnVisibility}
          clearSelectionTrigger={entity.table.clearSelectionTrigger}
          pageSize={entity.table.itemsPerPage}
          currentPage={entity.table.currentPage}
          onPageChange={entity.table.setCurrentPage}
        />
      </PageContent>

      <ColumnSelectorModal
        isOpen={entity.isColumnModalOpen}
        onClose={() => entity.setIsColumnModalOpen(false)}
        columns={columnOptions}
        onApply={handleColumnVisibilityApply}
      />

      <FiltersModal
        isOpen={entity.isFiltersModalOpen}
        onClose={() => entity.setIsFiltersModalOpen(false)}
        filterConfigs={entity.filterConfigs}
        activeFilters={entity.table.activeFilters}
        activeFilterMode={entity.table.filterMode}
        onApply={entity.table.setActiveFilters}
      />

      {/* ✅ OPTIMISATION: Monter la modale uniquement quand elle est ouverte */}
      {entity.isCreateModalOpen && (
        <CreateVehicleModal
          isOpen={entity.isCreateModalOpen}
          onClose={() => entity.setIsCreateModalOpen(false)}
          onSubmit={async (data) => {
            await entity.handleCreate(data);
          }}
        />
      )}

      <ConfirmDialog
        open={entity.deleteConfirm.open}
        onOpenChange={(open) => entity.setDeleteConfirm({ open })}
        title={
          entity.deleteConfirm.isBulk
            ? `Supprimer les ${VEHICLE_CONFIG.entityNamePlural}`
            : `Supprimer le ${VEHICLE_CONFIG.entityName}`
        }
        description={
          entity.deleteConfirm.isBulk
            ? `Êtes-vous sûr de vouloir supprimer ${entity.table.selectedItems.length} ${VEHICLE_CONFIG.entityName}(s) ?`
            : `Êtes-vous sûr de vouloir supprimer ce ${VEHICLE_CONFIG.entityName} ?`
        }
        confirmLabel="Supprimer"
        variant="destructive"
        onConfirm={entity.confirmDelete}
      />

      <ImportCSVModal
        open={entity.isImportModalOpen}
        onOpenChange={entity.setIsImportModalOpen}
        config={importConfig}
        mode={entity.importMode}
        onCreate={async (data) => {
          const { data: vehicle, error: supabaseError } = await supabase
            .from('vehicles')
            .insert([data as any])
            .select()
            .single();

          if (supabaseError) {
            throw supabaseError;
          }

          return vehicle;
        }}
        onUpdate={update}
        existingData={vehicles}
        entityName={VEHICLE_CONFIG.entityName}
        entityNamePlural={VEHICLE_CONFIG.entityNamePlural}
        onSuccess={entity.handleImportSuccess}
      />

      {/* Barre d'actions de masse */}
      {entity.table.selectedItems.length > 0 && (
        <BulkActionsBar
          count={entity.table.selectedItems.length}
          entityNamePlural={VEHICLE_CONFIG.entityNamePlural}
          onDelete={entity.handleBulkDeleteClick}
          onExportCSV={entity.handleExportSelectedCSV}
          onClearSelection={entity.table.clearSelection}
          showEmailAction={false}
          canDelete={canDeleteBulk('vehicle')}
          canExport={canExportData('vehicle')}
        />
      )}
    </PageLayout>
  );
}
